
import { useState } from 'react';
import { Phone, X, Hash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CountryFlag } from '@/components/ui/CountryFlag';
import { ContactBook, ContactBookSelection } from '@/components/shared/ContactBook';
import { cn } from '@/lib/utils';
import { formatPhoneNumber, detectCountryFromPhoneNumber } from '@/utils/phoneNumberFormatter';

interface UnifiedDialPadProps {
  mode: 'call' | 'dtmf';
  onCall?: (phoneNumber: string, contactInfo?: ContactBookSelection) => void;
  onSendDTMF?: (digit: string) => void;
  disabled?: boolean;
  phoneNumber?: string;
  onPhoneNumberChange?: (phoneNumber: string) => void;
  onContactSelect?: (contact: ContactBookSelection) => void;
  className?: string;
}

export const UnifiedDialPad = ({ 
  mode,
  onCall,
  onSendDTMF,
  disabled = false,
  phoneNumber: externalPhoneNumber,
  onPhoneNumberChange,
  onContactSelect,
  className
}: UnifiedDialPadProps) => {
  const [internalPhoneNumber, setInternalPhoneNumber] = useState('');
  const [dtmfDigits, setDtmfDigits] = useState('');
  const [selectedContact, setSelectedContact] = useState<ContactBookSelection | null>(null);
  
  // Use external phone number if provided, otherwise use internal state
  const phoneNumber = externalPhoneNumber !== undefined ? externalPhoneNumber : internalPhoneNumber;
  const setPhoneNumber = onPhoneNumberChange || setInternalPhoneNumber;
  
  // Format phone number as user types (only in call mode)
  const handleNumberChange = (input: string) => {
    if (mode !== 'call') return;
    
    // Clean input: remove all non-digit characters
    const digitsOnly = input.replace(/\D/g, '');
    
    // If no digits, clear the field
    if (digitsOnly.length === 0) {
      setPhoneNumber('');
      return;
    }
    
    // Detect country to get specific maximum length
    const country = detectCountryFromPhoneNumber(digitsOnly);
    const maxLength = country ? country.maxLength : 15;
    
    // Don't allow more digits than the country's maximum
    if (digitsOnly.length > maxLength) {
      return;
    }
    
    // Format the number based on detected country
    const result = formatPhoneNumber(digitsOnly);
    setPhoneNumber(result.formatted);
  };
  
  const handleKeypadPress = (digit: string) => {
    if (mode === 'call') {
      const currentDigits = phoneNumber.replace(/\D/g, '');
      const newDigits = currentDigits + digit;
      handleNumberChange(newDigits);
    } else if (mode === 'dtmf') {
      // Add to DTMF display and send immediately
      setDtmfDigits(prev => prev + digit);
      onSendDTMF?.(digit);
    }
  };
  
  const handleCall = () => {
    if (mode === 'call' && phoneNumber && onCall) {
      const result = formatPhoneNumber(phoneNumber);
      onCall(result.e164, selectedContact || undefined);
    }
  };

  const handleContactSelect = (contact: ContactBookSelection) => {
    setSelectedContact(contact);
    onContactSelect?.(contact);
  };
  
  const handleClearNumber = () => {
    if (mode === 'call') {
      setPhoneNumber('');
    } else {
      setDtmfDigits('');
    }
  };
  
  // Get formatting result for display (only in call mode)
  const formatResult = mode === 'call' ? formatPhoneNumber(phoneNumber) : null;
  
  const displayValue = mode === 'call' ? phoneNumber : dtmfDigits;
  const placeholder = mode === 'call' ? 'Enter phone number' : 'Enter extension...';
  
  return (
    <div className={cn("w-full max-w-xs mx-auto", className)}>
      {/* Contact Book - Only in call mode */}
      {mode === 'call' && (
        <div className="mb-4">
          <ContactBook
            mode="dialer"
            onContactSelect={handleContactSelect}
            onPhoneNumberChange={setPhoneNumber}
            placeholder="Search contacts by phone..."
          />
        </div>
      )}

      <div className="relative">
        <div className="flex items-center">
          {/* Country Flag (only in call mode) */}
          {mode === 'call' && formatResult?.country && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
              <CountryFlag country={formatResult.country} size="md" />
            </div>
          )}
          
          <Input
            type="tel"
            value={displayValue}
            onChange={(e) => mode === 'call' ? handleNumberChange(e.target.value) : undefined}
            readOnly={mode === 'dtmf'}
            className={cn(
              "text-lg h-12 text-center font-medium",
              mode === 'call' ? "pl-12 pr-10" : "px-4",
              mode === 'dtmf' && "font-mono"
            )}
            placeholder={placeholder}
          />
          
          {displayValue && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
              onClick={handleClearNumber}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        
        {/* Country info display (only in call mode) */}
        {mode === 'call' && formatResult?.country && (
          <div className="text-xs text-muted-foreground mt-1 text-center">
            {formatResult.country.name} • {formatResult.country.pattern}
          </div>
        )}
      </div>
    
      {/* Keypad */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map((digit) => (
          <Button 
            key={digit} 
            variant="outline" 
            className={cn(
              "h-12 text-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-800",
              "active:scale-95 transition-transform"
            )}
            onClick={() => handleKeypadPress(digit.toString())}
          >
            {digit}
          </Button>
        ))}
      </div>
      
      {/* Action Button (only in call mode) */}
      {mode === 'call' && (
        <>
          <Button 
            onClick={handleCall} 
            className="h-12 bg-green-500 hover:bg-green-600 text-white font-medium text-base w-full mt-4"
            disabled={!phoneNumber.trim() || disabled || !formatResult?.isValid}
          >
            <Phone className="mr-2 h-5 w-5" />
            Call
          </Button>
          
          {phoneNumber && !formatResult?.isValid && (
            <div className="text-xs text-red-500 mt-2 text-center">
              Invalid phone number format
            </div>
          )}
        </>
      )}
      
      {/* DTMF info (only in dtmf mode) */}
      {mode === 'dtmf' && (
        <div className="text-xs text-muted-foreground mt-2 text-center">
          Tap digits to send to active call
        </div>
      )}
    </div>
  );
};
